// import React from 'react'
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Company } from '../../types/api/Company';
import { Grid, ConfigContext, http, auth } from '../../base-ui/lib';
import { useGlobalDispatch, useGlobalState } from '../../stateProviders/GlobalStateProvider';
import Spinner from '../../globalComponents/spinner';
import SpinnerSize from '../../enums/spinnerSize';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditCompany from './editCompany';
import DeleteCompanyConfirmationModal from './deleteCompanyConfirmationModal';
import { GridActionsCellItem } from '@mui/x-data-grid';
import CompanyGridToolbar from './CompanyGridToolbar';
import EmptySearchState from '../../base-ui/Grid/components/EmptySearchState';

const EmptyStateComponent = () => <EmptySearchState />;

export const Companies = () => {
	const navigate = useNavigate();
	const { config }: any = React.useContext(ConfigContext);
	const gridRef = React.useRef<any>(null);
	const globalState = useGlobalState()
	const globalDispatch = useGlobalDispatch()
	const [switchingTenant, setSwitchingTenant] = useState(false)
	const [showEditCompany, setShowEditCompany] = useState<{ status: boolean; company?: Company }>({ status: false })

	// State for hybrid search
	const [filter, setFilter] = useState('');
	const [owner, setOwner] = useState('');
	const [allData, setAllData] = useState([]);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Memoized toolbar to prevent re-creation and focus loss
	const renderToolbar = useCallback(() => (
		<CompanyGridToolbar
			filter={filter}
			setFilter={setFilter}
			owner={owner}
			setOwner={setOwner}
			allData={allData}
			handleRefresh={() => gridRef.current?.onRefresh()}
			onAdd={null}
		/>
	), [filter, setFilter, owner, setOwner, allData]);

	const viewCompany = async (rowData: Company) => {
		if (rowData._id == globalState?.company?._id) {
			navigate(`/company/${rowData._id}`);
		} else {
			setSwitchingTenant(true)

			const platformJWT = await http.get({}, `${config.uri.base}/tenant/switch?tenantId=${rowData.tenantId}`, { withCredentials: true });
			auth.setBearerToken(platformJWT);

			setSwitchingTenant(false);

			window.location.href = `/company/${rowData._id}/profile`;
		}
	}

	const deleteCompany = (rowData: Company) => {
		globalDispatch({ modalOpen: true, modalContent: <DeleteCompanyConfirmationModal company={rowData} />
	})}

	const filterList = [
		{
			id: 'name',
			label: 'Company Name',
			dataType: 'string',
			defaultValue: '',
		},
		{
			id: 'owner',
			label: 'Owner',
			dataType: 'string',
			defaultValue: '',
		},
		{
			id: 'createdAt',
			label: 'Creation Date',
			dataType: 'date',
			defaultValue: '',
		},
	];

	const headCells = [
		{
			field: 'name',
			headerName: 'Company Name',
			flex: 2,
			sortable: true,
		},
		{
			field: 'owner',
			headerName: 'Owner',
			flex: 1.5,
			sortable: true,
		},
		{
			field: 'generatedQuotas',
			headerName: 'Generated Quotes',
			type: 'number',
			flex: 1,
		},
		{
			field: 'createdAt',
			headerName: 'Created',
			type: 'date',
			valueGetter: (value) => {
				if (!value) return new Date(0);
				const date = new Date(value);
				return isNaN(date.getTime()) ? new Date(0) : date;
			},
			sortable: true,
			flex: 1,
		},
		{
			field: 'actions',
			type: 'actions',
			flex: 0.7,
			getActions: ({row}) => [
				<GridActionsCellItem
					key="view"
					label="View"
					onClick={() => viewCompany(row)}
					showInMenu
				/>,
				<GridActionsCellItem
					key="edit"
					label="Edit"
					onClick={() => setShowEditCompany({ status: true, company: row })}
					showInMenu
				/>,
				<GridActionsCellItem
					key="delete"
					label="Delete"
					onClick={() => deleteCompany(row)}
					showInMenu
				/>,
			],
		},
	];

	return (
		<div className="flex flex-col gap-[20px] h-full">
			<Grid
				ref={gridRef}
				headCells={headCells}
				filterList={filterList}
				searchEndpont={`${config.uri.base}/company`}
				pageSizeOptions={[10, 25, 50]}
				selectableRows={false}
				hybridMode={true}
				initialPageSize={3000}
				toolbar={renderToolbar}
				showToolbar
				onDataLoad={setAllData}
				options={{ filter, owner }}
			customNoRowsOverlay={EmptyStateComponent}
			/>

			{switchingTenant &&
				<div className={`absolute ${isMobile ? 'left-0' : '-left-[300px]' } top-0 w-[100vw] h-[100vh] bg-black bg-opacity-80 flex items-center justify-center z-40`}>
					<Spinner size={SpinnerSize.LARGE}/>
				</div>
			}

			{showEditCompany.status && <EditCompany company={showEditCompany.company} onClose={()=> setShowEditCompany({ status: false })}  gridRef={gridRef} />}
		</div>
	);
};
